import React from 'react';
import { Link } from 'react-router-dom';
import { Cpu, CheckCircle, ArrowRight, Bot, FileCode, BarChart3, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

export const CustomSolutionsPage = () => {
  const { language } = useLanguage();

  const solutions = language === 'fr' ? [
    { icon: Bot, name: 'Agent IA de recherche juridique', desc: 'Chatbot interne connecté à vos bases documentaires et jurisprudentielles. Réponses sourcées, citations exactes, intégré à votre GED.', results: 'Recherche 5x plus rapide, taux de pertinence > 90%' },
    { icon: FileCode, name: 'Automatisation du drafting', desc: 'Génération automatisée de contrats types à partir de vos modèles et clauses. Personnalisation par matière, langue et juridiction.', results: 'Réduction de 60% du temps de rédaction' },
    { icon: BarChart3, name: 'Analyse documentaire IA', desc: 'Pipeline d\'extraction et de classification automatique de documents juridiques. Due diligence, analyse de portefeuille contractuel.', results: 'Traitement de 1000+ documents par jour' },
    { icon: Eye, name: 'Dashboard de veille réglementaire', desc: 'Monitoring automatisé des évolutions législatives et réglementaires sur vos matières. Alertes personnalisées, synthèses quotidiennes.', results: 'Veille exhaustive en temps réel' },
  ] : [
    { icon: Bot, name: 'Legal research AI agent', desc: 'Internal chatbot connected to your document and case law databases. Sourced answers, exact citations, integrated with your DMS.', results: '5x faster research, >90% relevance rate' },
    { icon: FileCode, name: 'Drafting automation', desc: 'Automated contract generation from your templates and clause libraries. Customization by practice area, language, and jurisdiction.', results: '60% reduction in drafting time' },
    { icon: BarChart3, name: 'AI document analysis', desc: 'Automated extraction and classification pipeline for legal documents. Due diligence, contract portfolio analysis.', results: '1000+ documents processed per day' },
    { icon: Eye, name: 'Regulatory watch dashboard', desc: 'Automated monitoring of legislative and regulatory changes in your practice areas. Custom alerts, daily summaries.', results: 'Comprehensive real-time monitoring' },
  ];

  const steps = language === 'fr' ? [
    { step: '01', title: 'Audit technique', desc: 'Analyse approfondie de vos processus existants, outils et besoins.' },
    { step: '02', title: 'Conception', desc: 'Architecture de la solution, choix des modèles IA, maquettage.' },
    { step: '03', title: 'Développement', desc: 'Construction de la solution, intégration avec vos outils (GED, LegalTech).' },
    { step: '04', title: 'Déploiement & formation', desc: 'Mise en production, formation des équipes, maintenance continue.' },
  ] : [
    { step: '01', title: 'Technical audit', desc: 'In-depth analysis of your existing processes, tools, and needs.' },
    { step: '02', title: 'Design', desc: 'Solution architecture, AI model selection, prototyping.' },
    { step: '03', title: 'Development', desc: 'Solution build, integration with your tools (DMS, LegalTech).' },
    { step: '04', title: 'Deployment & training', desc: 'Production launch, team training, ongoing maintenance.' },
  ];

  const faqs = language === 'fr' ? [
    { q: "Combien de temps prend un projet de solution IA sur-mesure ?", a: "De 2 à 6 mois selon la complexité. Un MVP peut être livré en 6 à 8 semaines." },
    { q: "Quels modèles d'IA utilisez-vous ?", a: "Nous sélectionnons les modèles les plus adaptés à votre cas d'usage, en fonction de vos exigences de confidentialité, de performance et de coût. Le choix s'opère parmi les solutions du marché, y compris les modèles open source." },
    { q: "La solution est-elle hébergée chez nous ?", a: "Selon vos exigences de sécurité, la solution peut être hébergée dans votre infrastructure (on-premise) ou dans un cloud privé dédié." },
    { q: "Quel est le ROI attendu ?", a: "Nos clients constatent un ROI positif en 3 à 6 mois en moyenne, avec des gains de productivité mesurables dès la mise en production." },
  ] : [
    { q: "How long does a custom AI solution project take?", a: "2 to 6 months depending on complexity. An MVP can be delivered in 6 to 8 weeks." },
    { q: "Which AI models do you use?", a: "We select the models best suited to your use case, based on your confidentiality, performance, and cost requirements. The choice is made among market solutions, including open source models." },
    { q: "Can the solution be hosted on-premise?", a: "Depending on your security requirements, the solution can be hosted in your infrastructure (on-premise) or in a dedicated private cloud." },
    { q: "What is the expected ROI?", a: "Our clients see positive ROI within 3 to 6 months on average, with measurable productivity gains from production launch." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={language === 'fr' ? "Solutions IA juridique sur-mesure | Gaius" : "Custom Legal AI Solutions | Gaius"}
        description={language === 'fr' ? "Conception et déploiement de solutions d'IA juridique personnalisées : agents IA, automatisation du drafting, analyse documentaire, veille réglementaire." : "Design and deployment of custom legal AI solutions: AI agents, drafting automation, document analysis, regulatory watch."}
        keywords={language === 'fr' ? "solution ia juridique, agent ia avocat, automatisation drafting juridique, ia sur mesure cabinet avocat" : "legal ai solution, lawyer ai agent, legal drafting automation, custom ai law firm"}
        canonicalUrl={language === 'fr' ? "https://gaius-tech.com/solution-ia-juridique-sur-mesure" : "https://gaius-tech.com/custom-legal-ai-solutions"}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {language === 'fr' ? 'Solutions IA juridique sur-mesure' : 'Custom legal AI solutions'}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            {language === 'fr'
              ? 'De l\'idée à la production : nous concevons et déployons des solutions d\'intelligence artificielle personnalisées pour votre cabinet ou direction juridique.'
              : 'From concept to production: we design and deploy custom AI solutions for your firm or legal department.'}
          </p>
          <Button asChild size="lg">
            <Link to="/contact">{language === 'fr' ? 'Discuter de votre projet' : 'Discuss your project'}</Link>
          </Button>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-10 text-center">
            {language === 'fr' ? 'Nos solutions' : 'Our solutions'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {solutions.map((sol, i) => (
              <div key={i} className="p-6 rounded-xl border border-border bg-gradient-to-br from-primary/5 to-accent/5 hover:shadow-md hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <sol.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">{sol.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{sol.desc}</p>
                <div className="flex items-center gap-2 text-sm text-primary font-medium">
                  <CheckCircle className="w-4 h-4" />
                  {sol.results}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-10 text-center">
            {language === 'fr' ? 'Notre méthodologie' : 'Our methodology'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">{s.step}</div>
                <h3 className="text-base font-semibold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border border-border px-6">
                <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-4">
            {language === 'fr' ? 'Lancez votre projet IA' : 'Launch your AI project'}
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            {language === 'fr' ? 'ROI garanti. Accompagnement de bout en bout.' : 'Guaranteed ROI. End-to-end support.'}
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/contact" className="flex items-center">
              {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
